import { useCallback, useContext, useState } from 'react';
import styled from '@emotion/styled';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import type { Chat } from '@trinity/chat';
import { ChatActionType } from '@trinity/chat';
import { SegmentChatContext } from '@trinity/chat-components/components/BrandInsights/SegmentChat.Context';
import { SubmitActionContext } from '@trinity/chat-components/components/Chat/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { CancelAction } from '../presentation/Message.QueryHint/CancelAction';
import { TextQueryHint } from '../presentation/Message.QueryHint/QueryHint';
import { Radio } from '../presentation/Radio';
import { useCancelSegmentChat } from './hooks/useCancelSegmentChat';

type Props = {
  queryIdentifier: string;
  value: Chat.QueryState.SegmentChatNeeded;
};

export const SegmentChatSelection = (props: Props) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { setSegmentChatStatus, setSegmentIconUrl, segmentIconsCache } = useContext(SegmentChatContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const submitAction = useContext(SubmitActionContext);

  const cancelMutation = useCancelSegmentChat({ queryIdentifier: props.queryIdentifier });

  const handleLaunchDigitalTwin = useCallback(() => {
    if (selectedId === null || !props.value.projectSlug) return;
    navigate(`/projects/${props.value.projectSlug}/hcp-digital-twins?segmentId=${selectedId}`);
  }, [selectedId, props.value.projectSlug, navigate]);

  const handleBrowseAll = useCallback(() => {
    if (!props.value.projectSlug) return;
    navigate(`/projects/${props.value.projectSlug}/hcp-digital-twins`);
  }, [props.value.projectSlug, navigate]);

  const handleCancel = useCallback(() => {
    return cancelMutation.mutateAsync();
  }, [cancelMutation]);

  const selectedSegment = props.value.segments.find((s) => s.id === selectedId);
  const segmentSelected = selectedId !== null;
  const canActOnSegment = segmentSelected && !!props.value.projectSlug && !cancelMutation.isPending;
  const canBrowse = !!props.value.projectSlug && !segmentSelected;

  const handleChatWithSegment = useCallback(() => {
    if (selectedId === null || !selectedSegment) return;

    const resolvedIconUrl =
      segmentIconsCache[selectedSegment.identifier] ?? selectedSegment.iconUrl ?? undefined;

    setSegmentChatStatus(true);
    setSegmentIconUrl(resolvedIconUrl);

    // The segment link lives on the chat context (`metadata.segment`); chat-api resolves the
    // persona and scoping tag from it, so no filter tag is injected client-side.
    dispatch({
      type: 'chat-instance-segment-updated',
      payload: { segment: { id: selectedSegment.identifier, name: selectedSegment.name } },
    });
    submitAction?.(
      {
        type: ChatActionType.SelectSegmentForChat,
        segmentIdentifier: selectedSegment.identifier,
        segmentId: selectedSegment.id,
        iconUrl: resolvedIconUrl,
      },
      `Chat with ${selectedSegment.name}`
    );
  }, [
    selectedId,
    selectedSegment,
    segmentIconsCache,
    setSegmentChatStatus,
    setSegmentIconUrl,
    dispatch,
    submitAction,
  ]);

  return (
    <Root>
      <HeaderCopy>{props.value.title ?? 'Ok, great! What segment would you like to chat with?'}</HeaderCopy>
      <PickCopy>Select Segment</PickCopy>
      <Segments>
        <RadioGroup
          value={selectedId === null ? '' : String(selectedId)}
          onChange={(_, value) => setSelectedId(Number(value))}
        >
          {props.value.segments.map((segment) => (
            <FormControlLabel
              key={segment.id}
              value={String(segment.id)}
              control={<Radio />}
              label={segment.name}
              sx={{ marginLeft: 0, gap: '6px', '&:not(:last-child)': { marginBottom: '12px' } }}
            />
          ))}
        </RadioGroup>
      </Segments>
      <Actions>
        {canActOnSegment && (
          <TextQueryHint value={`Chat with ${selectedSegment.name}`} onClick={handleChatWithSegment} />
        )}
        {canActOnSegment && (
          <TextQueryHint value="Launch Digital Twin" onClick={handleLaunchDigitalTwin} />
        )}
        {canBrowse && <TextQueryHint value="Browse all segments" onClick={handleBrowseAll} />}
        <CancelAction displayValue="Actually, I'd like to do something else." onClick={handleCancel} />
      </Actions>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;
`;

const HeaderCopy = styled.div`
  box-sizing: border-box;
  border-bottom: 1px solid ${(props) => props.theme.palette.gray.light1};
  padding-bottom: 10px;
  margin-bottom: 10px;
`;

const PickCopy = styled.div`
  font-size: 15px;
  font-family: ${(props) => props.theme.fonts.semiBold};
  margin-bottom: 10px;
`;

const Segments = styled.div`
  display: flex;
  flex-direction: column;

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const Actions = styled.div`
  margin-top: 10px;

  > :not(:last-child) {
    margin-bottom: 10px;
  }
`;
